import type {
  ColumnDef} from '@tanstack/react-table';
import {
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  useReactTable,
  type SortingState,
  type ColumnFiltersState,
  type PaginationState,
  type VisibilityState,
  type RowSelectionState,
  createColumnHelper,
} from '@tanstack/react-table';
import { ChevronDown, ChevronUp, ChevronsUpDown, MoreHorizontal, Check, Minus, ChevronLeft, ChevronRight } from 'lucide-react';
import * as React from 'react';
import { Button } from './button';
import { Checkbox } from './checkbox';
import { Input } from './input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './select';
import { cn } from '../../lib';

interface DataTableProps<TData, TValue> {
  columns: Array<ColumnDef<TData, TValue>>;
  data: TData[];
  searchKey?: string;
  filterableColumns?: Array<keyof TData>;
  sortBy?: SortingState;
  onSortChange?: (sorting: SortingState) => void;
  filters?: ColumnFiltersState;
  onFiltersChange?: (filters: ColumnFiltersState) => void;
  pagination?: PaginationState;
  onPaginationChange?: (pagination: PaginationState) => void;
  manualPagination?: boolean;
  rowCount?: number;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  columnVisibility?: VisibilityState;
  onColumnVisibilityChange?: (visibility: VisibilityState) => void;
  loading?: boolean;
  emptyMessage?: string;
  rowActions?: (row: TData) => React.ReactNode;
  className?: string;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchKey,
  filterableColumns = [],
  sortBy,
  onSortChange,
  filters,
  onFiltersChange,
  pagination,
  onPaginationChange,
  manualPagination,
  rowCount,
  rowSelection,
  onRowSelectionChange,
  columnVisibility,
  onColumnVisibilityChange,
  loading,
  emptyMessage = 'No hay datos disponibles',
  rowActions,
  className,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    state: {
      sorting: sortBy ?? [],
      columnFilters: filters ?? [],
      pagination: pagination ?? { pageIndex: 0, pageSize: 10 },
      rowSelection: rowSelection ?? {},
      columnVisibility: columnVisibility ?? {},
    },
    onSortingChange: (updater) => onSortChange?.(typeof updater === 'function' ? updater([]) : updater),
    onColumnFiltersChange: (updater) => onFiltersChange?.(typeof updater === 'function' ? updater([]) : updater),
    // El updater se aplica al estado ACTUAL (no a {0,10}): si no, «siguiente» desde la página 2 volvía a la 2 y reiniciaba pageSize.
    onPaginationChange: (updater) =>
      onPaginationChange?.(
        typeof updater === 'function' ? updater(pagination ?? { pageIndex: 0, pageSize: 10 }) : updater,
      ),
    onRowSelectionChange: (updater) => onRowSelectionChange?.(typeof updater === 'function' ? updater({}) : updater),
    onColumnVisibilityChange: (updater) => onColumnVisibilityChange?.(typeof updater === 'function' ? updater({}) : updater),
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    manualPagination: manualPagination ?? false,
    pageCount:
      manualPagination && rowCount !== undefined
        ? Math.max(1, Math.ceil(rowCount / (pagination?.pageSize ?? 10)))
        : undefined,
    ...(manualPagination ? {} : { getPaginationRowModel: getPaginationRowModel() }),
  });

  const totalRows = manualPagination ? (rowCount ?? data.length) : table.getFilteredRowModel().rows.length;

  return (
    <div className={cn('w-full', className)}>
      <div className="rounded-md border">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[600px] caption-bottom text-sm">
            <thead className="[&_tr]:border-b">
              {table.getHeaderGroups().map((headerGroup) => (
                <tr key={headerGroup.id} className="border-b bg-muted/40 transition-colors data-[state=selected]:bg-muted">
                  {headerGroup.headers.map((header) => (
                    <th
                      key={header.id}
                      className={cn(
                        'h-12 px-4 text-left align-middle text-xs font-semibold uppercase tracking-wide text-muted-foreground [&:has([role=checkbox])]:pr-0',
                        header.column.getCanSort() && 'cursor-pointer select-none hover:text-foreground'
                      )}
                    >
                      {header.isPlaceholder ? null : (
                        <div className="flex items-center space-x-2">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                          {header.column.getCanSort() && (
                            <span className="ml-auto">
                              {header.column.getIsSorted() === 'asc' ? (
                                <ChevronUp className="h-4 w-4" />
                              ) : header.column.getIsSorted() === 'desc' ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronsUpDown className="h-4 w-4 text-muted-foreground" />
                              )}
                            </span>
                          )}
                          {header.column.getCanFilter() && (
                            <div className="ml-auto flex items-center gap-1">
                              <Input
                                placeholder="Filtrar..."
                                value={(header.column.getFilterValue() as string) ?? ''}
                                onChange={(e) => header.column.setFilterValue(e.target.value)}
                                className="h-8 w-32 text-xs"
                              />
                            </div>
                          )}
                        </div>
                      )}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody className="[&_tr:last-child]:border-0">
              {loading ? (
                <tr>
                  <td colSpan={columns.length} className="h-24 text-center">
                    <div className="flex items-center justify-center h-full">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
                    </div>
                  </td>
                </tr>
              ) : data.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                table.getRowModel().rows.map((row) => (
                  <tr
                    key={row.id}
                    className={cn(
                      'border-b transition-colors hover:bg-muted/30 data-[state=selected]:bg-muted',
                      row.getIsSelected() && 'bg-primary/5'
                    )}
                    onClick={row.getToggleSelectedHandler()}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        className={cn(
                          'p-4 align-middle tabular-nums [&:has([role=checkbox])]:pr-0',
                          cell.column.getCanFilter() && 'min-w-[120px]'
                        )}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                    {rowActions && (
                      <td className="p-4 align-middle">
                        <div className="flex items-center justify-end gap-2">
                          {rowActions(row.original)}
                        </div>
                      </td>
                    )}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <div className="border-t px-4 py-3 sm:px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-muted-foreground">
                Mostrando {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} a{' '}
                {Math.min(
                  (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                  totalRows
                )}{' '}
                de {totalRows}
              </span>
              <Select
                value={table.getState().pagination.pageSize.toString()}
                onValueChange={(value) => table.setPageSize(Number(value))}
              >
                <SelectTrigger className="w-[70px]">
                  <SelectValue placeholder="Filas" />
                </SelectTrigger>
                <SelectContent>
                  {[10, 20, 30, 40, 50].map((size) => (
                    <SelectItem key={size} value={size.toString()}>
                      {size}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="icon"
                onClick={() => table.previousPage()}
                disabled={!table.getCanPreviousPage()}
                aria-label="Página anterior"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => table.nextPage()}
                disabled={!table.getCanNextPage()}
                aria-label="Página siguiente"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export { createColumnHelper };